import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { API_BASE } from '../config';
import { readJsonWithOnboarding } from '../onboarding/notify-onboarding-update';

type UserFeedbackFormProps = {
  onClose?: () => void;
  onSubmitted?: () => void;
  showCancelButton?: boolean;
  autoFocus?: boolean;
  className?: string;
};

export const UserFeedbackForm: React.FC<UserFeedbackFormProps> = ({
  onClose,
  onSubmitted,
  showCancelButton = true,
  autoFocus = true,
  className,
}) => {
  const { token } = useAuth();
  const { t } = useLocale();
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const handleSubmitFeedback = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!feedbackMessage.trim() || isSubmittingFeedback || !token) {
      return;
    }

    setIsSubmittingFeedback(true);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };

      const response = await fetch(`${API_BASE}/api/user-feedback`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: feedbackMessage.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      await readJsonWithOnboarding(response);
      setFeedbackMessage('');
      onSubmitted?.();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert(t('failed_to_submit_feedback'));
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleFeedbackKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Escape') {
      onClose?.();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmitFeedback(e);
    }
  };

  if (!token) {
    return null;
  }

  return (
    <form onSubmit={handleSubmitFeedback} className={className}>
      <textarea
        value={feedbackMessage}
        onChange={(e) => setFeedbackMessage(e.target.value)}
        onKeyDown={handleFeedbackKeyDown}
        placeholder={t('feedback_placeholder')}
        disabled={isSubmittingFeedback}
        autoFocus={autoFocus}
        className="w-full min-h-[150px] p-3 border border-input rounded-md text-sm resize-y outline-none focus:border-primary mb-4 disabled:cursor-not-allowed disabled:opacity-50 bg-background text-foreground placeholder:text-muted-foreground"
      />
      <div className="flex gap-2 justify-end flex-wrap">
        {showCancelButton && onClose && (
          <CancelButton
            type="button"
            onClick={onClose}
            disabled={isSubmittingFeedback}
           />
        )}
        <Button
          type="submit"
          className="bg-[var(--scribane-green)] text-white hover:opacity-90 shadow border-0"
          disabled={isSubmittingFeedback || !feedbackMessage.trim()}
        >
          {isSubmittingFeedback ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('sending')}
            </>
          ) : (
            t('submit_feedback')
          )}
        </Button>
      </div>
    </form>
  );
};
